import React, {useEffect, useState} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {useQuery,useMutation, useApolloClient} from '@apollo/client';
import {CharacterQuery,CharacterQueryData,CharacterQueryVars, CharacterGamesQuery, CharacterGamesQueryData, CharacterMapQuery,CharacterMapQueryData,
CharacterPlayedWithQuery, CharacterPlayedWithQueryData, CharacterDuelsQuery, CharacterDuelsQueryData, CharacterDuelQueryVars, CharacterDuelQueryData, CharacterDuelQuery} from '@pvplevelup/core/dist/queries/character/character';
import { RouteComponentProps } from 'react-router-dom';
import { Tabs, Tab, Typography, Box, useMediaQuery  } from '@material-ui/core';
import {BarChart, Map, Games, Group, SportsKabaddi, Explore, EmojiEvents, Stars} from '@material-ui/icons'
import { CharacterPlayedWith } from 'components/Character/CharacterPlayedWith';
import { CharacterStats, breakPoint } from 'components/Character/CharacterStats';
import { CharacterMaps } from 'components/Character/CharacterMaps';
import { CharacterGames } from 'components/Character/CharacterGames';
import { CharacterHeader } from 'components/Character/CharacterHeader';
import moment from 'moment';
import { DuelsTab } from 'views/Duel/DuelsTab';
import { PveRunList } from 'views/Pve/run/PveRunList';
import { TournamentHistory } from 'views/Tournament/TournamentHistory';
import { FeatBuild } from 'components/Feats/FeatBuild';
import { CharacterFilters } from 'components/Character/CharacterFilters';
import _ from 'lodash'
import { ProfileTabPanel as TabPanel, MobileFilters, useProfileLayoutStyles } from 'components/Profile/ProfileLayout';
import { useIsMobile } from 'components/Page/useIsMobile';
import { useMountedState } from 'components/useMountedState';
import { CharacterDuels } from 'components/Character/CharacterDuels';
import { LeaderboardSeasonsData, LeaderboardSeasonsQuery } from '@pvplevelup/core/dist/queries/leaderboard/seasons';
import { ISeason } from '@pvplevelup/core/dist/interfaces/ISeason';

const useStyles = makeStyles(theme => ({
  // the app bar is 56px on phones (48px in landscape) and 64px from the sm breakpoint up
  page: {
    height: 'calc(100vh - 56px)',
    '@media (orientation: landscape)': {
      height: 'calc(100vh - 48px)',
    },
    [theme.breakpoints.up('sm')]: {
      height: 'calc(100vh - 64px)',
    },
  },
  nav: {
    padding: theme.spacing(1.25, 0, 1.25, 1.25),
  },
  tabsRoot: {
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 8,
    padding: theme.spacing(0.5, 0),
  },
  indicator: {
    width: 3,
    borderRadius: 2,
  },
  tab: {
    minWidth: 'auto',
    minHeight: 44,
    padding: theme.spacing(0.5, 2),
    textTransform: 'none',
    fontSize: '0.92rem',
    opacity: 0.75,
    '&.Mui-selected': {
      opacity: 1,
      background: 'rgba(255, 255, 255, 0.05)',
    },
    '&:hover': {
      opacity: 1,
    },
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
    '& .MuiTab-wrapper > *:first-child': {
      marginBottom: 0,
      marginRight: theme.spacing(1.25),
      fontSize: '1.25rem',
    },
  },
  content: {
    padding: theme.spacing(1.25, 1.5),
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    // the stat cards and tables inside each tab
    '& .MuiPaper-root': {
      background: 'rgba(20, 20, 20, 0.92)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: 8,
      boxShadow: 'none',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid rgba(255,255,255,0.05)',
    },
  },
  filtersPanel: {
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 8,
    padding: theme.spacing(1.5, 1.5, 0.5),
    marginBottom: theme.spacing(2),
  },
  filtersTitle: {
    fontSize: '0.75em',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.65,
    margin: theme.spacing(0, 0, 1, 1),
  },
}));

interface Props{
}

interface RouteParams {
  id?: string;
}

export enum FetchType {
  Games,
  PlayedWith
}

const Character: React.FC<Props & RouteComponentProps<RouteParams>> = (props) => {
  const classes = useStyles();
  const apolloClient = useApolloClient();
  const [season, setSeason] = useState<number>(1);
  const [seasons, setSeasons] = useState<ISeason[]>([]);
  const [ranked, setRanked] = useState("Ranked");
  const [zones, setZones] = useState<number[]>([]);
  const [maps, setMaps] = useState<number[]>([]);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [sizes, setSizes] = useState<number[]>([]);
  const [modes, setModes] = useState<string[]>([]);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [filtersDefault, setFiltersDefault] = useState(true);
  const [byCharacter, setByCharacter] = useState<boolean>(true);
  const isMounted = useMountedState();


  const { data:{character = null,characterStats,classStats,fieldStats} = {},loading, error, refetch,client } = useQuery<CharacterQueryData,CharacterQueryVars>(
    CharacterQuery,
    { variables: {character: props.match.params.id || "", ranked: ranked,season:season}, fetchPolicy: 'cache-and-network'},
  );

  const { data:{characterMapStats} = {}, loading: mapLoading, fetchMore: mapFetchMore, refetch: mapRefetch } = useQuery<CharacterMapQueryData,CharacterQueryVars>(
    CharacterMapQuery,
    { variables: {character: props.match.params.id || "", ranked: ranked,season:season}, fetchPolicy: 'cache-and-network' },
  );

  const { data:{characterPlayedWithStats} = {}, loading: playedWithLoading, fetchMore: playedWithFetchMore, refetch: playedWithRefetch } = useQuery<CharacterPlayedWithQueryData,CharacterQueryVars>(
    CharacterPlayedWithQuery,
    { variables: {character: props.match.params.id || "", ranked: ranked,season:season,byCharacter:byCharacter}, fetchPolicy: 'cache-and-network' },
  );

  const { data:{gamesByCharacter} = {}, loading: gamesLoading, fetchMore: gamesFetchMore, refetch: gamesRefetch } = useQuery<CharacterGamesQueryData,CharacterQueryVars>(
    CharacterGamesQuery,
    { variables: {character: props.match.params.id || "", ranked: ranked,season:season}, fetchPolicy: 'cache-and-network' },
  );

  useEffect(()=>{
    if(isMounted()){
      apolloClient.query<LeaderboardSeasonsData>({query:LeaderboardSeasonsQuery}).then((res)=>{
        if(res.data){
          if(res.data.currentSeason){
            setSeason(res.data.currentSeason.season);
          }
          setSeasons(res.data.seasons || []);
        }
      })
    }
  },[])

  const onChangeRanked = (value: string) => {
    setFiltersChanged(true);
    setRanked(value);
  }

  const onChangeZones = (value: number[]) => {
    setFiltersChanged(true);
    setZones(value);
  }

  const onChangeMaps = (value: number[]) => {
    setFiltersChanged(true);
    setMaps(value);
  }

  const onChangeStartDate = (value: string) => {
    setFiltersChanged(true);
    setStartDate(value);
  }

  const onChangeEndDate = (value: string) => {
    setFiltersChanged(true);
    setEndDate(value);
  }

  const onChangeSizes = (value: number[]) => {
    setFiltersChanged(true);
    setSizes(value);
  }

  const onChangeModes = (value: string[]) => {
    setFiltersChanged(true);
    setModes(value);
  }

  const onChangeSeason = (value: number) => {
    setFiltersChanged(true);
    setSeason(value);
  }

  const onChangeByCharacter = (value: boolean) => {
    setByCharacter(value);
    let variables:CharacterQueryVars = {character: props.match.params.id || "",ranked:ranked,season:season};
    if(zones) variables.zones = zones;
    if(maps) variables.maps = maps;
    variables.startDate = startDate;
    variables.endDate = endDate;
    if(sizes) variables.sizes = sizes;
    if(modes) variables.modes = modes;
    playedWithRefetch({...variables,byCharacter:value});
  }

  const onRefetch = (ranked:string,zones:number[],maps:number[],startDate:string | null,endDate:string | null,sizes:number[],modes:string[],filtersChanged:boolean) => {
    if(filtersChanged){
      let variables:CharacterQueryVars = {character: props.match.params.id || "",ranked:ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      let duelVariables: any = {...variables};
      delete duelVariables.sizes;
      delete duelVariables.modes;
      delete duelVariables.maps;

      setFiltersChanged(false);
      setFiltersDefault(false);
      refetch(variables);
      mapRefetch(variables);
      gamesRefetch(variables);
      playedWithRefetch({...variables,byCharacter:byCharacter});
    }
  }

  const onResetFilters = () => {
    setRanked("Ranked");
    setZones([]);
    setMaps([]);
    setStartDate(null);
    setEndDate(null);
    setSizes([]);
    setModes([]);
    if(!filtersDefault){
      onRefetch("Ranked",[],[],null,null,[],[],true);
      setFiltersDefault(true);
    }
  }

  //const {data:{character = null,characterMapStats,characterPlayedWithStats,gamesByCharacter,characterStats,classStats} = {},loading, error, fetchMore} = result;
  //console.log(characterMapStats,characterPlayedWithStats,gamesByCharacter,characterStats);

  const fetchMoreMapsRecords = ()=>{
    if(!mapLoading && characterMapStats?.hasNext){
      let variables:CharacterQueryVars = {character: props.match.params.id || "",ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      variables.next = characterMapStats.next;
      mapFetchMore({
        variables:{...variables,},
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;

          let characterMapStats = [...prev.characterMapStats?.maps || [], ...fetchMoreResult.characterMapStats?.maps || []];
          characterMapStats = _.reverse(_.sortBy(characterMapStats,(map)=>map.played));
          characterMapStats = _.uniqBy(characterMapStats,(map)=>map.map);

          return Object.assign({}, prev, {
            characterMapStats:{
              next: fetchMoreResult.characterMapStats?.next,
              previous: fetchMoreResult.characterMapStats?.previous,
              hasNext: fetchMoreResult.characterMapStats?.hasNext,
              hasPrevious: fetchMoreResult.characterMapStats?.next,
              maps: characterMapStats,
              __typename: fetchMoreResult.characterMapStats?.__typename
            }
          });
        }
      })
    }
  }

  const fetchMoreGameRecords = ()=>{
    if(!gamesLoading && gamesByCharacter?.hasNext){
      let variables:CharacterQueryVars = {character: props.match.params.id || "",ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      variables.next = gamesByCharacter.next;
      gamesFetchMore({
        variables:{...variables,},
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;

          let gamesByCharacter = [...prev.gamesByCharacter?.games || [], ...fetchMoreResult.gamesByCharacter?.games || []];
          gamesByCharacter = _.reverse(_.sortBy(gamesByCharacter,(game)=>game.createdAt));
          gamesByCharacter = _.uniqBy(gamesByCharacter,(game)=>game._id);

          return Object.assign({}, prev, {
            gamesByCharacter:{
              next: fetchMoreResult.gamesByCharacter?.next,
              previous: fetchMoreResult.gamesByCharacter?.previous,
              hasNext: fetchMoreResult.gamesByCharacter?.hasNext,
              hasPrevious: fetchMoreResult.gamesByCharacter?.next,
              games: gamesByCharacter,
              __typename: fetchMoreResult.gamesByCharacter?.__typename
            }
          });
        }
      })
    }
  }

  const fetchMorePlayedWithRecords = ()=>{
    if(!playedWithLoading && characterPlayedWithStats?.hasNext){
      let variables:CharacterQueryVars = {character: props.match.params.id || "",ranked,season:season};
      if(zones) variables.zones = zones;
      if(maps) variables.maps = maps;
      variables.startDate = startDate;
      variables.endDate = endDate;
      variables.byCharacter = byCharacter;
      if(sizes) variables.sizes = sizes;
      if(modes) variables.modes = modes;
      variables.next = characterPlayedWithStats.next;
      playedWithFetchMore({
        variables:{...variables,},
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult) return prev;
          let characterPlayedWithStats = [...prev.characterPlayedWithStats?.stats || [], ...fetchMoreResult.characterPlayedWithStats?.stats || []];
          //characterPlayedWithStats = _.reverse(_.sortBy(characterPlayedWithStats,(map)=>map.played));
          characterPlayedWithStats = _.uniqBy(characterPlayedWithStats,(map)=>byCharacter ? map.playedWith?._id : map.playedWithUser?._id);

          return Object.assign({}, prev, {
            characterPlayedWithStats:{
              next: fetchMoreResult.characterPlayedWithStats?.next,
              previous: fetchMoreResult.characterPlayedWithStats?.previous,
              hasNext: fetchMoreResult.characterPlayedWithStats?.hasNext,
              hasPrevious: fetchMoreResult.characterPlayedWithStats?.next,
              stats: characterPlayedWithStats,
              __typename: fetchMoreResult.characterPlayedWithStats?.__typename
            }
          });
        }
      })
    }
  }

  //console.log(seasons);
  
  const [activeTab, setActiveTab] = useState(0);
  const DUELS_TAB = 4;
  const DUNGEONS_TAB = 5;
  const TOURNAMENTS_TAB = 6;
  const FEATS_TAB = 7;
  const matches = useMediaQuery(`(max-width:${breakPoint}px)`);

  const mobile = useIsMobile();
  const layout = useProfileLayoutStyles();
  const [showFilters, setShowFilters] = useState(false);
  const activeFilters = (ranked != "Ranked" ? 1 : 0) + (zones.length ? 1 : 0) + (maps.length ? 1 : 0) + (startDate || endDate ? 1 : 0) + (sizes.length ? 1 : 0) + (modes.length ? 1 : 0);
  const hasFilters = activeTab != DUELS_TAB && activeTab != DUNGEONS_TAB && activeTab != FEATS_TAB;

  const profile = (
    <CharacterHeader characterStats={characterStats} fieldStats={fieldStats} character={character} lastGame={(gamesByCharacter && gamesByCharacter.games && gamesByCharacter.games[0]) ? moment(gamesByCharacter.games[0].createdAt).format("ddd, MMM Do YYYY, h:mm a") : ""} />
  );
  const filters = (
    <CharacterFilters filtersChanged={filtersChanged} ranked={ranked} zones={zones} maps={maps} startDate={startDate} endDate={endDate} sizes={sizes} modes={modes}
      resetFilters={onResetFilters} applyFilters={onRefetch} onChangeRanked={onChangeRanked} onChangeZones={onChangeZones}
      onChangeMaps={onChangeMaps} onChangeStartDate={onChangeStartDate} onChangeEndDate={onChangeEndDate} onChangeSizes={onChangeSizes} onChangeModes={onChangeModes}
      onChangeSeason={onChangeSeason} seasons={seasons} season={season}
    />
  );

  return (
    <div className={mobile ? layout.mobilePage : classes.page}>
      {(!error) &&
        <div className="character-container"
          style={mobile ? {display: 'block', height: 'auto', overflow: 'visible'} : {
            gridTemplateColumns: matches ? '1fr' : '11rem 1fr',
            gridTemplateRows: matches ? 'max-content minmax(0, 1fr)' : 'minmax(0, 1fr)',
            height: '100%',
            gridTemplateAreas: matches ? `'character-tabs' 'character-content'` : `'character-tabs character-content'`,
          }}
        >
          {mobile && profile}
          <div className={mobile ? layout.mobileTabs : `character-tabs ${classes.nav}`}>
            <Tabs
              orientation={matches ? "horizontal" :"vertical"}
              variant="scrollable"
              scrollButtons={matches && !mobile ? "auto" : "off"}
              value={activeTab}
              onChange={(e,value)=>setActiveTab(value)}
              aria-label=""
              classes={{root: classes.tabsRoot, indicator: classes.indicator}}

            >
              <Tab className={classes.tab} icon={<BarChart />} label="Stats" />
              <Tab className={classes.tab} icon={<Map />} label="Maps" />
              <Tab className={classes.tab} icon={<Games />} label="Game History" />
              <Tab className={classes.tab} icon={<Group />} label="Played With" />
              <Tab className={classes.tab} icon={<SportsKabaddi />} label="Duels" />
              <Tab className={classes.tab} icon={<Explore />} label="Dungeons" />
              <Tab className={classes.tab} icon={<EmojiEvents />} label="Tournaments" />
              <Tab className={classes.tab} icon={<Stars />} label="Feats" />
            </Tabs>
          </div>
          <div className={mobile ? layout.mobileContent : `character-content ${classes.content}`}>
            {mobile ?
              hasFilters &&
                <MobileFilters active={activeFilters} open={showFilters} onToggle={() => setShowFilters(!showFilters)}>{filters}</MobileFilters>
            :
              <div className="character-header" style={{flexShrink: 0}}>
                {profile}
                {hasFilters &&
                  <div className={classes.filtersPanel}>
                    <div className={classes.filtersTitle}>Filters</div>
                    {filters}
                  </div>
                }
              </div>
            }
            <TabPanel value={activeTab} index={0} mobile={mobile}>
              <CharacterStats character={character} stats={characterStats} classStats={classStats} mapStats={characterMapStats} gamesByCharacter={gamesByCharacter} />
            </TabPanel>
            <TabPanel value={activeTab} index={1} mobile={mobile} fill>
              <CharacterMaps mapFeed={characterMapStats} fetchMoreRecords={fetchMoreMapsRecords} />
            </TabPanel>
            <TabPanel value={activeTab} index={2} mobile={mobile} fill>
              <CharacterGames character={character} gameFeed={gamesByCharacter} fetchMoreRecords={fetchMoreGameRecords} />
            </TabPanel>
            <TabPanel value={activeTab} index={3} mobile={mobile} fill>
              <CharacterPlayedWith playedWithFeed={characterPlayedWithStats} fetchMoreRecords={fetchMorePlayedWithRecords} byCharacter={byCharacter} onChangeByCharacter={onChangeByCharacter} />
            </TabPanel>
            <TabPanel value={activeTab} index={DUELS_TAB} mobile={mobile}>
              <DuelsTab characterId={props.match.params.id} />
            </TabPanel>
            <TabPanel value={activeTab} index={DUNGEONS_TAB} mobile={mobile}>
              <PveRunList characterId={props.match.params.id} />
            </TabPanel>
            <TabPanel value={activeTab} index={TOURNAMENTS_TAB} mobile={mobile}>
              <TournamentHistory characterId={props.match.params.id} />
            </TabPanel>
            <TabPanel value={activeTab} index={FEATS_TAB} mobile={mobile}>
              <FeatBuild spec={character?.spec} />
            </TabPanel>
          </div>
        </div>
      }
    </div>
  )
}

export {Character};